import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { ChatAnswer, ChatMessage, ChatRequestError } from 'modules/chat/domain/interfaces';
import chatResource from 'modules/chat/domain/resources/chat-resource';
import { RootState } from 'store/store';

const PREFIX = 'chat';

type ChatThunkConfig = { rejectValue: ChatRequestError };

const STATUS_FALLBACK_MESSAGES: Record<number, string> = {
  403: 'Недостаточно прав для использования ИИ-ассистента в этом турнире.',
  503: 'Сервис ИИ-ассистента временно недоступен, попробуйте позже.',
};

const extractError = (error: unknown, prefix: string): ChatRequestError => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown; code?: unknown; error?: unknown; remainingTokens?: unknown } | undefined;
    const httpStatus = error.response?.status;
    const bodyCode = typeof data?.code === 'number' ? data.code : undefined;
    const status =
      httpStatus ?? (data?.error === 'FEATURE_REQUIRED' ? 403 : undefined) ?? bodyCode;

    if (status === 429) {
      if (data?.remainingTokens !== undefined) {
        return {
          message: 'Превышен месячный лимит токенов ИИ-ассистента. Попробуйте позже.',
          status,
        };
      }

      return { message: 'Слишком много запросов. Попробуйте позже.', status };
    }

    const serverMessage = typeof data?.message === 'string' ? data.message : undefined;
    const fallback = status ? STATUS_FALLBACK_MESSAGES[status] : undefined;

    if (serverMessage) return { message: serverMessage, status };
    if (fallback) return { message: fallback, status };
  }

  console.error(`${prefix} error:`, error);
  return { message: error instanceof Error ? error.message : String(error) };
};

const getActiveTournamentId = (getState: () => unknown): number => {
  const { auth } = getState() as RootState;
  const activeTournamentId = auth.activeTournamentId;

  if (!activeTournamentId) {
    throw new Error('Нет активного турнира');
  }

  return activeTournamentId;
};

export const sendChatMessage = createAsyncThunk<ChatAnswer, string, ChatThunkConfig>(
  `${PREFIX}/sendChatMessage`,
  async (message, { rejectWithValue, getState }) => {
    try {
      const tournamentId = getActiveTournamentId(getState);
      return await chatResource.sendMessage(tournamentId, {
        message,
        timezoneOffsetMinutes: -new Date().getTimezoneOffset(),
      });
    } catch (error) {
      return rejectWithValue(extractError(error, `${PREFIX}/sendChatMessage`));
    }
  },
);

export const loadChatHistory = createAsyncThunk<ChatMessage[], void, ChatThunkConfig>(
  `${PREFIX}/loadChatHistory`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const tournamentId = getActiveTournamentId(getState);
      return await chatResource.getHistory(tournamentId);
    } catch (error) {
      return rejectWithValue(extractError(error, `${PREFIX}/loadChatHistory`));
    }
  },
);

export const clearChatHistory = createAsyncThunk<void, void, ChatThunkConfig>(
  `${PREFIX}/clearChatHistory`,
  async (_, { rejectWithValue, getState }) => {
    try {
      const tournamentId = getActiveTournamentId(getState);
      await chatResource.clearHistory(tournamentId);
    } catch (error) {
      return rejectWithValue(extractError(error, `${PREFIX}/clearChatHistory`));
    }
  },
);
